import { Input, Checkbox, Select, Button, Space, Tooltip } from "antd";
import { SearchOutlined, TableOutlined, AppstoreOutlined, ReloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  gisIdentityStatusShortLabel,
  gisPositionStateLabel,
  type GisIdentityStatus,
  type GisPositionState,
} from "./gisVocabulary";
import "./gisAdmin.css";

/**
 * Thứ tự hiển thị là HỢP ĐỒNG THỊ GIÁC, không sắp theo số lượng — cùng luật với dải DANH TÍNH
 * ở màn hồ sơ (`IdentityStrip.tsx`), để hai chỗ đọc ra cùng một thứ tự.
 */
const IDENTITY_FILTER_ORDER: readonly GisIdentityStatus[] = ["KNOWN", "PARTIAL", "UNKNOWN"];
const POSITION_STATE_ORDER: readonly GisPositionState[] = ["OCCUPIED", "AVAILABLE", "CLOSED"];

export interface GisFilterBarProps {
  readonly search: string;
  readonly onSearchChange: (val: string) => void;
  readonly identityFilters: Readonly<Record<GisIdentityStatus, boolean>>;
  readonly onIdentityFilterChange: (key: GisIdentityStatus, val: boolean) => void;
  readonly positionState: GisPositionState | "ALL";
  readonly onPositionStateChange: (val: GisPositionState | "ALL") => void;
  readonly isTableView: boolean;
  readonly onToggleTableView: () => void;
  readonly onRefresh?: (() => void) | undefined;
  readonly onToggleDetail?: (() => void) | undefined;
  readonly isDetailOpen?: boolean | undefined;
}

export function GisFilterBar({
  search,
  onSearchChange,
  identityFilters,
  onIdentityFilterChange,
  positionState,
  onPositionStateChange,
  isTableView,
  onToggleTableView,
  onRefresh,
  onToggleDetail,
  isDetailOpen,
}: GisFilterBarProps) {
  return (
    <div className="gis-filter-bar">
      {/* Tìm kiếm mã vị trí */}
      <div style={{ flex: "1 1 260px", maxWidth: 320 }}>
        <Input
          prefix={<SearchOutlined style={{ color: "var(--ncc-color-text-secondary)" }} />}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Tìm theo mã vị trí..."
          allowClear
          size="middle"
          aria-label="Tìm theo mã vị trí"
        />
      </div>

      {/* Bộ lọc danh tính & trạng thái vị trí. Khoá + nhãn + màu đều lấy từ gisVocabulary
          (DB thắng) — xem ghi chú lịch sử ở đầu file đó. */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <Space size="small">
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ncc-color-text-secondary)" }}>
            Danh tính:
          </span>
          {/* KHÔNG chấm màu ở đây. Chấm màu từng lặp lại đúng ba swatch của dải chú giải nằm
              ngay dưới bản đồ — hai bản sao của cùng một khoá màu, cách nhau vài chục pixel.
              Chú giải là chỗ mắt đọc khi đang nhìn bản đồ, nên khoá ở lại đó; hộp kiểm giữ
              nhãn chữ, đủ nghĩa để lọc. */}
          {IDENTITY_FILTER_ORDER.map((code) => (
            <Checkbox
              key={code}
              checked={identityFilters[code]}
              onChange={(e) => onIdentityFilterChange(code, e.target.checked)}
            >
              {gisIdentityStatusShortLabel(code)}
            </Checkbox>
          ))}
        </Space>

        <Space size="small">
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ncc-color-text-secondary)" }}>
            Trạng thái:
          </span>
          <Select
            value={positionState}
            onChange={(val) => onPositionStateChange(val as GisPositionState | "ALL")}
            style={{ width: 180 }}
            aria-label="Lọc theo trạng thái vị trí"
            options={[
              { label: "Tất cả vị trí", value: "ALL" },
              ...POSITION_STATE_ORDER.map((code) => ({
                label: gisPositionStateLabel(code),
                value: code,
              })),
            ]}
          />
        </Space>

        {/* Nút làm mới dữ liệu bản đồ */}
        {onRefresh ? (
          <Tooltip title="Làm mới">
            <Button
              icon={<ReloadOutlined />}
              onClick={onRefresh}
              aria-label="Làm mới"
            />
          </Tooltip>
        ) : null}

        {/* Nút chuyển đổi sang danh sách bảng fallback */}
        <Button
          icon={isTableView ? <AppstoreOutlined /> : <TableOutlined />}
          onClick={onToggleTableView}
          aria-label={isTableView ? "Xem bản đồ GIS" : "Xem dạng bảng"}
        >
          {isTableView ? "Xem bản đồ GIS" : "Danh sách dạng bảng"}
        </Button>

        {/* Nút bật/tắt ngăn kéo chi tiết mộ (chế độ nhúng) */}
        {onToggleDetail ? (
          <Button
            icon={<InfoCircleOutlined />}
            onClick={onToggleDetail}
            aria-label={isDetailOpen ? "Ẩn chi tiết vị trí mộ" : "Xem chi tiết vị trí mộ"}
            type={isDetailOpen ? "primary" : "default"}
          >
            Chi tiết mộ
          </Button>
        ) : null}
      </div>
    </div>
  );
}
